import { createSlice } from "@reduxjs/toolkit"
import overviewAPI from "./overviewAPI"
import { createAppAsyncThunk } from "app/hooks"
import {
  IOverviewState,
  IOverviewWaterState,
  IWeeklyOverviewPayload,
} from "./types"
import { resetStoreAction } from "config"
import { prepareWeeklyOverviewData } from "./helper"

let getWeeklyOverviewAbortController: AbortController | null = null

export const getWeeklyOverview = createAppAsyncThunk(
  "overview/getWeeklyOverview",
  async (payload: IWeeklyOverviewPayload, { rejectWithValue }) => {
    if (getWeeklyOverviewAbortController) {
      getWeeklyOverviewAbortController.abort()
    }

    const localController = new AbortController()
    getWeeklyOverviewAbortController = localController

    try {
      const response = await overviewAPI.getWeeklyOverview(
        payload,
        localController.signal,
      )

      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getWeeklyOverviewAbortController === localController) {
        getWeeklyOverviewAbortController = null
      }
    }
  },
)

let getWeeklyWaterAbortController: AbortController | null = null

export const getWeeklyWater = createAppAsyncThunk(
  "overview/getWeeklyWater",
  async (payload: IWeeklyOverviewPayload, { rejectWithValue }) => {
    if (getWeeklyWaterAbortController) {
      getWeeklyWaterAbortController.abort()
    }

    const localController = new AbortController()
    getWeeklyWaterAbortController = localController

    try {
      const response = await overviewAPI.getWeeklyWater(
        payload,
        localController.signal,
      )

      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getWeeklyWaterAbortController === localController) {
        getWeeklyWaterAbortController = null
      }
    }
  },
)

const initialState: IOverviewState & IOverviewWaterState = {
  getWeeklyOverviewLoading: false,
  weeklyOverview: undefined,
  getWeeklyOverviewFailed: null,

  getWeeklyWaterLoading: false,
  weeklyWater: undefined,
  getWeeklyWaterFailed: null,
}

export const overviewSlice = createSlice({
  name: "overview",
  initialState,
  reducers: {
    resetWeeklyData(state) {
      state.weeklyOverview = undefined
      state.getWeeklyOverviewLoading = false
      state.getWeeklyOverviewFailed = null
      state.weeklyWater = undefined
      state.getWeeklyWaterLoading = false
      state.getWeeklyWaterFailed = null
    },
  },
  extraReducers(builder) {
    builder.addCase(getWeeklyOverview.pending, (state) => {
      state.getWeeklyOverviewLoading = true
      state.getWeeklyOverviewFailed = null
    }),
      builder.addCase(getWeeklyOverview.fulfilled, (state, { payload }) => {
        state.getWeeklyOverviewLoading = false
        state.weeklyOverview = prepareWeeklyOverviewData(payload)
      }),
      builder.addCase(getWeeklyOverview.rejected, (state, action) => {
        if ((action.payload as any)?.aborted) return
        state.getWeeklyOverviewLoading = false
        state.getWeeklyOverviewFailed = action.payload
      })

    builder.addCase(getWeeklyWater.pending, (state) => {
      state.getWeeklyWaterLoading = true
      state.getWeeklyWaterFailed = null
    })
    builder.addCase(getWeeklyWater.fulfilled, (state, { payload }) => {
      state.getWeeklyWaterLoading = false
      state.weeklyWater = payload
    })
    builder.addCase(getWeeklyWater.rejected, (state, action) => {
      if ((action.payload as any)?.aborted) return
      state.getWeeklyWaterLoading = false
      state.getWeeklyWaterFailed = action.payload
    })

    builder.addCase(resetStoreAction, () => {
      return initialState
    })
  },
})

export const { resetWeeklyData } = overviewSlice.actions
export default overviewSlice.reducer
